import axios, { AxiosError } from "axios";
import { clearAuthSession, getAuthToken } from "./authSession";

/**
 * Ticket: TypeScript API service layer for backend communication
 * Stepwise TODO (wird pro Schritt aktualisiert):
 * [x] 1) Zentralen Axios Client + Base URL Konfiguration einführen
 * [x] 2) API Response Interfaces erweitern und generische Error-Typen einführen
 * [x] 3) Request/Response Interceptors einführen
 * [x] 4) Retry-Logik (kontrolliert, für geeignete Requests) implementieren
 * [x] 5) Loading State Management im Service ergänzen
 */

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, "");
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function deriveShortUrlBaseFromApiBase(apiBaseUrl: string): string {
  if (!isAbsoluteHttpUrl(apiBaseUrl)) {
    return window.location.origin;
  }

  return apiBaseUrl.replace(/\/api$/, "");
}

const API_BASE_URL = "https://api-ls.eddy.rip/api";
const SHORT_URL_BASE = normalizeBaseUrl(
  process.env.REACT_APP_SHORT_URL_BASE || "https://api-ls.eddy.rip",
);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    Accept: "application/json",
  },
});

export type ApiErrorCode =
  | "network_error"
  | "timeout"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "rate_limited"
  | "validation_error"
  | "server_error"
  | "unknown_error";

export type ApiError = {
  code: ApiErrorCode;
  message: string;
  status?: number;
  retryable?: boolean;
  details?: unknown;
  validationIssues?: ApiValidationIssue[];
};

export type ApiValidationIssue = {
  field: string;
  message: string;
  type?: string;
};

export type FastApiValidationDetail = {
  loc: Array<string | number>;
  msg: string;
  type: string;
};

export type RetryMode = "never" | "safe" | "always";

export type RetryOptions = {
  retries?: number;
  retryMode?: RetryMode;
  retryDelayMs?: number;
};

type RetryableAxiosRequestConfig = {
  method?: string;
  _retryCount?: number;
  retryOptions?: RetryOptions;
};

type FastApiErrorResponse = {
  detail?: string | FastApiValidationDetail[];
  message?: string;
  [key: string]: unknown;
};

export type LoginResponse = {
  access_token: string;
  token_type: string;
};

export type RegisterPayload = {
  username: string;
  email: string;
  password: string;
};

export type UserResponse = {
  id: number;
  username: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
};

export type UrlResponse = {
  id: number;
  user_id: number;
  original_url: string;
  short_url: string;
  created_at: string;
  expires_at?: string | null;
  click_count: number;
  is_active: boolean;
};

export type ClickResponse = {
  id: number;
  url_id: number;
  ip_address: string;
  useragent: string;
  referer?: string | null;
  clicked_at: string;
};

export type UserStatsResponse = {
  total_urls: number;
  total_clicks: number;
  active_urls: number;
};

export type CreateUrlPayload = {
  original_url: string;
  custom_short_url?: string;
  expires_at?: string | null;
};

export type ApiListResponse<T> = T[];

export type UrlItem = UrlResponse;
export type UrlClick = ClickResponse;

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  retries: 2,
  retryMode: "safe",
  retryDelayMs: 300,
};

const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);
let pendingRequestCount = 0;
const loadingListeners = new Set<(count: number) => void>();
let unauthorizedRedirectInProgress = false;

function notifyLoadingListeners(): void {
  loadingListeners.forEach((listener) => listener(pendingRequestCount));
}

function incrementPendingRequests(): void {
  pendingRequestCount += 1;
  notifyLoadingListeners();
}

function decrementPendingRequests(): void {
  pendingRequestCount = Math.max(0, pendingRequestCount - 1);
  notifyLoadingListeners();
}

export function getPendingRequestCount(): number {
  return pendingRequestCount;
}

export function isApiLoading(): boolean {
  return pendingRequestCount > 0;
}

export function subscribeToApiLoading(
  listener: (count: number) => void,
): () => void {
  loadingListeners.add(listener);
  listener(pendingRequestCount);
  return () => {
    loadingListeners.delete(listener);
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function resolveRetryOptions(
  config?: RetryableAxiosRequestConfig,
): Required<RetryOptions> {
  return {
    retries: config?.retryOptions?.retries ?? DEFAULT_RETRY_OPTIONS.retries,
    retryMode:
      config?.retryOptions?.retryMode ?? DEFAULT_RETRY_OPTIONS.retryMode,
    retryDelayMs:
      config?.retryOptions?.retryDelayMs ?? DEFAULT_RETRY_OPTIONS.retryDelayMs,
  };
}

function isMethodRetryAllowed(
  method: string | undefined,
  mode: RetryMode,
): boolean {
  if (mode === "never") return false;
  if (mode === "always") return true;

  const normalizedMethod = (method || "GET").toUpperCase();
  return (
    normalizedMethod === "GET" ||
    normalizedMethod === "HEAD" ||
    normalizedMethod === "OPTIONS"
  );
}

function isRetryableError(error: AxiosError): boolean {
  if (error.code === "ERR_CANCELED") return false;
  if (error.code === "ECONNABORTED") return true;

  const status = error.response?.status;
  if (!status) return true;
  return RETRYABLE_STATUS_CODES.has(status);
}

function getRetryDelayMs(
  currentRetryCount: number,
  retryDelayMs: number,
): number {
  const exponentialDelay = retryDelayMs * Math.pow(2, currentRetryCount);
  const jitter = Math.floor(Math.random() * 100);
  return exponentialDelay + jitter;
}

function handleUnauthorizedResponse(): void {
  clearAuthSession();

  if (typeof window === "undefined") {
    return;
  }

  if (unauthorizedRedirectInProgress) {
    return;
  }

  const currentHash = window.location.hash || "#/";
  if (currentHash.startsWith("#/login")) {
    return;
  }

  unauthorizedRedirectInProgress = true;
  window.location.hash = "#/login";

  window.setTimeout(() => {
    unauthorizedRedirectInProgress = false;
  }, 300);
}

export function getShortRedirectUrl(shortCode: string): string {
  if (isAbsoluteHttpUrl(shortCode)) {
    return shortCode;
  }

  const cleanCode = shortCode.replace(/^\/+/, "");
  return `${SHORT_URL_BASE}/${cleanCode}`;
}

function mapStatusToErrorCode(status?: number): ApiErrorCode {
  if (!status) return "network_error";
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 429) return "rate_limited";
  if (status === 422) return "validation_error";
  if (status >= 500) return "server_error";
  return "unknown_error";
}

function parseFastApiValidationIssues(
  data: FastApiErrorResponse,
): ApiValidationIssue[] | undefined {
  if (!Array.isArray(data.detail)) return undefined;

  const issues = data.detail
    .filter((entry): entry is FastApiValidationDetail => {
      return (
        typeof entry === "object" &&
        entry !== null &&
        Array.isArray(entry.loc) &&
        typeof entry.msg === "string"
      );
    })
    .map((entry) => ({
      field: entry.loc.map(String).join("."),
      message: entry.msg,
      type: entry.type,
    }));

  return issues.length > 0 ? issues : undefined;
}

function extractErrorMessage(
  status: number | undefined,
  code: string | undefined,
  data: FastApiErrorResponse | undefined,
): string {
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  const detail = data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .filter((entry): entry is FastApiValidationDetail => {
        return (
          typeof entry === "object" &&
          entry !== null &&
          typeof entry.msg === "string" &&
          entry.msg.trim().length > 0
        );
      })
      .map((entry) => entry.msg);
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (code === "ECONNABORTED") {
    return "Die Anfrage hat zu lange gedauert.";
  }

  if (!status) {
    return "Netzwerkfehler. Bitte Verbindung prüfen und erneut versuchen.";
  }

  return "Die Anfrage konnte nicht verarbeitet werden.";
}

function normalizeApiError(error: AxiosError): ApiError {
  const status = error.response?.status;
  const responseData =
    (error.response?.data as FastApiErrorResponse | undefined) || undefined;
  const apiCode =
    error.code === "ECONNABORTED" ? "timeout" : mapStatusToErrorCode(status);

  return {
    code: apiCode,
    message: extractErrorMessage(status, error.code, responseData),
    status,
    retryable: !status || status === 429 || status >= 500,
    details: responseData,
    validationIssues: parseFastApiValidationIssues(responseData || {}),
  };
}

apiClient.interceptors.request.use((config) => {
  incrementPendingRequests();
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization =
      `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    decrementPendingRequests();
    return response;
  },
  async (error: unknown) => {
    decrementPendingRequests();

    if (axios.isAxiosError(error)) {
      const retryConfig = error.config as
        | RetryableAxiosRequestConfig
        | undefined;
      const status = error.response?.status;
      if (status === 401) {
        handleUnauthorizedResponse();
      }

      const retryOptions = resolveRetryOptions(retryConfig);
      const currentRetryCount = retryConfig?._retryCount ?? 0;

      const shouldRetry =
        Boolean(retryConfig) &&
        currentRetryCount < retryOptions.retries &&
        isMethodRetryAllowed(retryConfig?.method, retryOptions.retryMode) &&
        isRetryableError(error);

      if (shouldRetry && retryConfig) {
        retryConfig._retryCount = currentRetryCount + 1;
        const delayMs = getRetryDelayMs(
          currentRetryCount,
          retryOptions.retryDelayMs,
        );
        await wait(delayMs);
        return apiClient.request(retryConfig);
      }

      return Promise.reject(normalizeApiError(error));
    }

    const unknownError: ApiError = {
      code: "unknown_error",
      message: "Ein unerwarteter Fehler ist aufgetreten.",
      retryable: false,
      details: error,
    };
    return Promise.reject(unknownError);
  },
);

export async function shortenUrl(url: string): Promise<string> {
  const res = await apiClient.post<UrlResponse>("/urls", {
    original_url: url,
  });
  return getShortRedirectUrl(res.data.short_url);
}

export async function loginUser(
  usernameOrEmail: string,
  password: string,
): Promise<LoginResponse> {
  const payload = new URLSearchParams();
  payload.set("username", usernameOrEmail);
  payload.set("password", password);

  const res = await apiClient.post<LoginResponse>("/auth/login", payload, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });

  return res.data;
}

export async function registerUser(
  payload: RegisterPayload,
): Promise<UserResponse> {
  const res = await apiClient.post<UserResponse>("/auth/register", payload);
  return res.data;
}

export async function getMyUrls(skip = 0, limit = 100): Promise<UrlItem[]> {
  const res = await apiClient.get<UrlItem[]>("/urls", {
    params: { skip, limit },
  });
  return res.data;
}

export async function createUrl(payload: CreateUrlPayload): Promise<UrlItem> {
  const res = await apiClient.post<UrlItem>("/urls", payload);
  return res.data;
}

export async function deleteUrl(urlId: number): Promise<void> {
  await apiClient.delete(`/urls/${urlId}`);
}

export async function getMyStats(): Promise<UserStatsResponse> {
  const res = await apiClient.get<UserStatsResponse>("/auth/stats");
  return res.data;
}

export async function getUrlClicks(
  urlId: number,
  skip = 0,
  limit = 100,
): Promise<UrlClick[]> {
  const res = await apiClient.get<UrlClick[]>(`/urls/${urlId}/clicks`, {
    params: { skip, limit },
  });
  return res.data;
}

// Backward-compatible aliases for existing consumers.
export const fetchMyUrls = getMyUrls;
export const fetchUrlClicks = getUrlClicks;
